import React, { useState, useEffect } from 'react';
import { X, UserPlus, Search, User, MapPin, Briefcase } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import type { User as AppUser, Project, ProfileType } from '../../types';

interface ProjectAssignmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  project: Project;
  onSuccess: () => void;
}

export const ProjectAssignmentModal: React.FC<ProjectAssignmentModalProps> = ({
  isOpen,
  onClose,
  project,
  onSuccess
}) => {
  const [advisors, setAdvisors] = useState<AppUser[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAdvisor, setSelectedAdvisor] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      loadAdvisors();
      setSelectedAdvisor(project.assigned_advisor_id || '');
      setSearchTerm('');
      setError('');
    }
  }, [isOpen, project.assigned_advisor_id]);

  const loadAdvisors = async () => {
    setIsLoading(true);
    try {
      // Try to load from database first
      let advisorsData: AppUser[] = [];
      
      try {
        const { data, error } = await supabase
          .from('users')
          .select('*')
          .eq('profile_type', 'ACCOMPAGNATEUR')
          .order('full_name');

        if (error) throw error;
        advisorsData = data || [];
      } catch (dbError) {
        console.warn('Could not load advisors from database, using mock data:', dbError);
        // Fallback to mock data
        advisorsData = [
          {
            id: '550e8400-e29b-41d4-a716-446655440010',
            email: 'sophie.martin@cress-guadeloupe.org',
            profile_type: 'ACCOMPAGNATEUR' as ProfileType,
            full_name: 'Sophie Martin',
            phone: '0590 45 67 89',
            organization: 'CRESS Guadeloupe',
            address: '12 Rue de la Liberté',
            city: 'Basse-Terre',
            postal_code: '97100',
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z'
          },
          {
            id: '550e8400-e29b-41d4-a716-446655440011',
            email: 'pierre.dubois@ess-conseil.gp',
            profile_type: 'ACCOMPAGNATEUR' as ProfileType,
            full_name: 'Pierre Dubois',
            phone: '0590 56 78 90',
            organization: 'ESS Conseil Guadeloupe',
            address: '5 Boulevard Chanzy',
            city: 'Pointe-à-Pitre',
            postal_code: '97110',
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z'
          },
          {
            id: '550e8400-e29b-41d4-a716-446655440012',
            email: 'isabelle.leroy@accompagnement-ess.gp',
            profile_type: 'ACCOMPAGNATEUR' as ProfileType,
            full_name: 'Isabelle Leroy',
            phone: '0590 67 89 01',
            organization: 'Accompagnement ESS',
            address: '18 Rue Schoelcher',
            city: 'Le Gosier',
            postal_code: '97190',
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z'
          },
          {
            id: '550e8400-e29b-41d4-a716-446655440013',
            email: 'marie.charles@cress-guadeloupe.org',
            profile_type: 'ACCOMPAGNATEUR' as ProfileType,
            full_name: 'Marie-France Charles',
            phone: '0590 78 90 12',
            organization: 'CRESS Guadeloupe',
            address: '12 Rue de la Liberté',
            city: 'Basse-Terre',
            postal_code: '97100',
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z'
          },
          {
            id: '550e8400-e29b-41d4-a716-446655440014',
            email: 'jean.martin@pfil-guadeloupe.org',
            profile_type: 'ACCOMPAGNATEUR' as ProfileType,
            full_name: 'Jean-Claude Martin',
            phone: '0590 89 01 23',
            organization: 'PFIL Guadeloupe',
            address: 'ZAC de Rivière Roche',
            city: 'Basse-Terre',
            postal_code: '97100',
            created_at: '2024-01-01T00:00:00Z',
            updated_at: '2024-01-01T00:00:00Z'
          }
        ];
      }

      setAdvisors(advisorsData);
    } catch (err: any) {
      setError('Erreur lors du chargement des accompagnateurs: ' + err.message);
      console.error('Error loading advisors:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!selectedAdvisor) {
      setError('Veuillez sélectionner un accompagnateur');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('projects')
        .update({ 
          assigned_advisor_id: selectedAdvisor,
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);

      if (error) throw error;

      // Create notification for the assigned advisor
      const selectedAdvisorData = advisors.find(a => a.id === selectedAdvisor);
      if (selectedAdvisorData) {
        await supabase
          .from('notifications_enhanced')
          .insert({
            user_id: selectedAdvisor,
            type: 'PROJECT_ASSIGNED',
            title: 'Nouveau projet assigné',
            message: `Le projet "${project.name}" vous a été assigné`,
            link: `/projects/${project.id}`,
            metadata: {
              project_id: project.id,
              project_name: project.name
            }
          });
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de l\'assignation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnassign = async () => {
    setIsSubmitting(true);
    setError('');

    try {
      const { error } = await supabase
        .from('projects')
        .update({ 
          assigned_advisor_id: null,
          updated_at: new Date().toISOString()
        })
        .eq('id', project.id);

      if (error) throw error;

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erreur lors de la désassignation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredAdvisors = advisors.filter(advisor => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      advisor.full_name?.toLowerCase().includes(searchLower) ||
      advisor.email?.toLowerCase().includes(searchLower) ||
      advisor.organization?.toLowerCase().includes(searchLower)
    );
  });

  const getProfileTypeColor = (type: string) => {
    return 'bg-blue-100 text-blue-800';
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <UserPlus className="w-6 h-6 text-blue-600" />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Assigner un accompagnateur</h2>
              <p className="text-sm text-gray-600">{project.name}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Current Assignment */}
          {project.assigned_advisor && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2">Accompagnateur actuel</h3>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {project.assigned_advisor.full_name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-blue-900">
                      {project.assigned_advisor.full_name || project.assigned_advisor.email}
                    </p>
                    <p className="text-sm text-blue-700">{project.assigned_advisor.organization}</p>
                  </div>
                </div>
                <button
                  onClick={handleUnassign}
                  disabled={isSubmitting}
                  className="px-3 py-1 text-red-600 hover:bg-red-50 rounded-md transition-colors text-sm"
                >
                  Désassigner
                </button>
              </div>
            </div>
          )}

          {/* Search */}
          <div>
            <label htmlFor="advisor-search" className="block text-sm font-medium text-gray-700 mb-2">
              Rechercher un accompagnateur
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                id="advisor-search"
                type="text"
                placeholder="Nom, email ou organisation..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Advisors List */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Accompagnateurs disponibles ({filteredAdvisors.length})
            </h3>
            
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filteredAdvisors.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <User className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                <p>
                  {searchTerm 
                    ? 'Aucun accompagnateur trouvé avec ces critères'
                    : 'Aucun accompagnateur disponible'
                  }
                </p>
              </div>
            ) : (
              <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                {filteredAdvisors.map((advisor) => (
                  <label
                    key={advisor.id}
                    className="flex items-center p-4 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                  >
                    <input
                      type="radio"
                      name="advisor"
                      value={advisor.id}
                      checked={selectedAdvisor === advisor.id}
                      onChange={(e) => setSelectedAdvisor(e.target.value)}
                      className="mr-3"
                    />
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                        <span className="text-white font-medium text-sm">
                          {advisor.full_name?.charAt(0) || advisor.email?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <p className="font-medium text-gray-900">
                            {advisor.full_name || advisor.email}
                          </p>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getProfileTypeColor(advisor.profile_type || '')}`}>
                            Accompagnateur
                          </span>
                        </div>
                        {advisor.organization && (
                          <p className="text-sm text-gray-600">{advisor.organization}</p>
                        )}
                        <div className="flex items-center space-x-4 text-xs text-gray-500 mt-1">
                          {advisor.city && (
                            <div className="flex items-center space-x-1">
                              <MapPin className="w-3 h-3" />
                              <span>{advisor.city}</span>
                            </div>
                          )}
                          {advisor.phone && (
                            <div className="flex items-center space-x-1">
                              <span>{advisor.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Annuler
            </button>
            <button
              onClick={handleAssign}
              disabled={!selectedAdvisor || isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Assignation...</span>
                </div>
              ) : (
                'Assigner'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};